import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { UnitsStore } from '@store';

import { convertToFahrenheitRounded } from '../../../temperature/converter/converter';

@Component({
  selector: 'app-heating-curve',
  templateUrl: './heating-curve.component.html',
  styleUrls: ['./heating-curve.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeatingCurveComponent implements OnInit, DoCheck, OnDestroy {
  @ViewChild('container') container: ElementRef;

  isCelsius: boolean;
  degree: string;
  labelUnit: string;

  data: { x: number; y: number }[] = [];

  minOutsideTemperature = -30;
  maxOutsideTemperature = 20;

  gapX = 10;
  gapY = 20;

  graphWidth = 450;
  graphHeight = 388;

  outTempStep = 10;

  bindConvertToFahrenheit: Function;

  private width$: BehaviorSubject<number> = new BehaviorSubject<number>(window.innerWidth);
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  @Input() isE3HeatPumpDevice: boolean;

  @Input() slope: number;

  @Input() shift: number;

  @Input() roomTemperature;

  @Input() isHeatPumpFirstCH = false;

  @Input() minFlowLimitTemperature: number;

  @Input() maxFlowLimitTemperature: number;

  @Input() isFlowTemperatureAvailable: boolean;

  @HostListener('window:resize')
  onResize(): void {
    this.width$.next(this.container.nativeElement.offsetWidth);
  }

  constructor(private cdr: ChangeDetectorRef, private unitStore: UnitsStore) {
    this.degree = '°';
    this.unitStore.isTemperatureMetric$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isCelsius) => (this.isCelsius = isCelsius));
  }

  ngOnInit(): void {
    this.bindConvertToFahrenheit = this.convertToFahrenheitRounded.bind(this);
    this.labelUnit = this.isCelsius ? 'C' : 'F';
    this.onWidthChange();
    setTimeout(() => {
      this.width$.next(this.container.nativeElement.offsetWidth);
    }, 100);
  }

  ngDoCheck(): void {
    this.calculateHeatCurve();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onWidthChange(): void {
    this.width$.pipe(takeUntil(this.ngUnsubscribe), debounceTime(1000)).subscribe((width) => {
      if (width > 1279) {
        this.graphWidth = 450;
      } else {
        this.graphWidth = width - 25;
      }

      if (width >= 1279) {
        this.graphHeight = 388;
      } else if (width >= 767) {
        this.graphHeight = 246;
      }

      this.cdr.markForCheck();
    });
  }

  calculateHeatCurve(): void {
    this.data = [];

    let alfa: number;
    let flowTemperature: number;

    for (
      let i = this.maxOutsideTemperature;
      i >= this.minOutsideTemperature;
      i -= this.outTempStep
    ) {
      alfa = i - this.roomTemperature;

      if (this.isE3HeatPumpDevice) {
        flowTemperature =
          this.roomTemperature +
          this.shift -
          this.slope * alfa * (1.4347 + 2.1e-2 * alfa + 247.9e-6 * (alfa * alfa));
      } else if (this.isHeatPumpFirstCH) {
        flowTemperature =
          this.roomTemperature +
          this.shift -
          this.slope * alfa * (1.148987 + 2.1e-2 * alfa + 247.9e-6 * (alfa * alfa)) +
          5;
      } else {
        flowTemperature =
          this.roomTemperature +
          this.shift -
          this.slope * alfa * (1.4347 + 2.1e-2 * alfa + 247.9e-6 * (alfa * alfa));
      }

      // temperatures below 'minFlowLimitTemperature' will be shown as 'minFlowLimitTemperature'
      // and temperatures above 'maxFlowLimitTemperature' will be shown as 'maxFlowLimitTemperature'
      if (flowTemperature < this.minFlowLimitTemperature) {
        flowTemperature = this.minFlowLimitTemperature;
      } else if (flowTemperature > this.maxFlowLimitTemperature) {
        flowTemperature = this.maxFlowLimitTemperature;
      }

      this.data.push({
        x: i,
        y: flowTemperature,
      });
    }
  }

  convertToFahrenheitRounded(temperature: number): number {
    if (this.isCelsius) {
      return temperature;
    }

    return convertToFahrenheitRounded(temperature);
  }
}
